import React, { useLayoutEffect, useState } from "react";
import Navbar from "../home/Navbar/Navbar";
import Footer from "../Footer/Footer";
import ReCAPTCHA from "react-google-recaptcha";

export default function ContactPage() {
	const [form, setForm] = useState({
		brandName: "Azure",
		firstName: "",
		email: "",
		phone: "",
		message: "",
	});

	const [errors, setErrors] = useState({});
	const [status, setStatus] = useState(null);
	const [showRecaptcha, setShowRecaptcha] = useState(false);

	useLayoutEffect(() => {
		window.scrollTo({ top: 0, behavior: "smooth" });
	}, []);

	// --- Validation ---
	function validate() {
		const e = {};
		if (!form.firstName.trim()) e.firstName = "First name is required";
		if (!form.email.trim()) e.email = "Email is required";
		else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email))
			e.email = "Enter a valid email";
		if (!form.phone.trim()) e.phone = "Phone Number is required";
		if (!form.message.trim()) e.message = "Message is required";
		return e;
	}

	// Handle input change
	const handleChange = (e) => {
		const { name, value } = e.target;
		setForm((f) => ({ ...f, [name]: value }));

		if (errors[name]) {
			setErrors((prev) => ({ ...prev, [name]: "" }));
		}
	};

	// Button click → Show reCAPTCHA
	const handleSubmit = (e) => {
		e.preventDefault();

		const validation = validate();
		setErrors(validation);

		if (Object.keys(validation).length) return;

		// Only show reCAPTCHA after user submits
		setShowRecaptcha(true);
		setStatus("waiting-captcha");
	};

	// After user solves reCAPTCHA → send API request
	const handleCaptchaVerify = async (token) => {
		if (!token) return;

		try {
			setStatus("sending");

			const formData = {
				name: form.firstName,
				email: form.email,
				phone: form.phone,
				message: form.message,
				brandName: form.brandName,
				recaptchaToken: token,
			};

			const response = await fetch(
				"https://admin.monsinidress.com/api/contact",
				{
					method: "POST",
					headers: { "Content-Type": "application/json" },
					body: JSON.stringify(formData),
				}
			);

			const result = await response.json();

			if (result.success) {
				setStatus("success");
				setForm({
					brandName: "Azure",
					firstName: "",
					email: "",
					phone: "",
					message: "",
				});
				setShowRecaptcha(false);

				setTimeout(() => setStatus(null), 5000);
			} else {
				throw new Error(result.message);
			}
		} catch (err) {
			console.error(err);
			setStatus("error");
		}
	};

	const getInputClasses = (field) => {
		const base =
			"mt-1 block w-full rounded-lg border px-4 py-3 shadow-sm transition-shadow focus:outline-none focus:ring-2 focus:ring-zinc-800 bg-white placeholder-gray-400";
		return errors[field]
			? `${base} border-red-500`
			: `${base} border-gray-300 hover:border-gray-400`;
	};

	return (
		<>
			<Navbar />

			<div className="mt-[180px] min-h-screen bg-gray-100 py-20 px-4 sm:px-6 lg:px-10">
				<div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
					{/* LEFT SECTION */}
					<section className="bg-white p-10 rounded-xl shadow-2xl border border-gray-200">
						<h1 className="text-4xl font-americana font-bold text-[#8a459f] mb-2 tracking-wide">
							Contact Us
						</h1>
						<p className="text-gray-600 mb-8 text-lg">
							For inquiries about our bespoke elegant gowns, please reach out.
							We respond within 24 hours.
						</p>

						<form onSubmit={handleSubmit} className="space-y-6">
							{/* First Name */}
							<div>
								<label className="block text-sm font-semibold text-gray-700">
									Your First Name
								</label>
								<input
									name="firstName"
									value={form.firstName}
									onChange={handleChange}
									type="text"
									placeholder="Enter your first name"
									className={getInputClasses("firstName")}
								/>
								{errors.firstName && (
									<p className="text-red-600 text-sm">{errors.firstName}</p>
								)}
							</div>

							{/* Email */}
							<div>
								<label className="block text-sm font-semibold text-gray-700">
									Your Email Address*
								</label>
								<input
									name="email"
									value={form.email}
									onChange={handleChange}
									type="email"
									placeholder="name@example.com"
									className={getInputClasses("email")}
								/>
								{errors.email && (
									<p className="text-red-600 text-sm">{errors.email}</p>
								)}
							</div>
							<div>
								<label className="block text-sm font-semibold text-gray-700">
									Your Phone Number
								</label>
								<input
									name="phone"
									value={form.phone}
									onChange={handleChange}
									type="phone"
									placeholder="(XXX) XXX XXX "
									className={getInputClasses("phone")}
								/>
								{errors.phone && (
									<p className="text-red-600 text-sm">{errors.phone}</p>
								)}
							</div>

							{/* Message */}
							<div>
								<label className="block text-sm font-semibold text-gray-700">
									Your Message*
								</label>
								<textarea
									name="message"
									value={form.message}
									onChange={handleChange}
									rows={6}
									className={getInputClasses("message")}
									placeholder="Type your message here..."
								/>
								{errors.message && (
									<p className="text-red-600 text-sm">{errors.message}</p>
								)}
							</div>

							{/* reCAPTCHA only after clicking Submit */}
							{showRecaptcha && (
								<div className="mt-4">
									<ReCAPTCHA
										// sitekey="6LdB_x8sAAAAAICDUzNCdH6LopcCC90OOkGmRMrI"
										sitekey="6Lc8fiwsAAAAAEnGQbIZaoT-y5EPeaK87c03G494"
										// secret Key  6Lc8fiwsAAAAAHJaPt3AnrCf44BiNu6mbl84fIol
										onChange={handleCaptchaVerify}
									/>
									{status === "waiting-captcha" && (
										<p className="text-red-600 text-sm mt-2">
											Please complete the reCAPTCHA to continue.
										</p>
									)}
								</div>
							)}

							{/* Submit Button */}
							<div className="pt-2 flex items-center space-x-4">
								<button
									type="submit"
									className="inline-flex items-center text-white justify-center rounded-lg bg-zinc-800 px-6 py-3 text-base font-bold shadow-md hover:bg-zinc-700"
									disabled={status === "sending"}>
									{status === "sending" ? "Sending..." : "Send Your Inquiry"}
								</button>

								{status === "success" && (
									<p className="text-green-600 font-medium">
										✓ Your inquiry has been sent!
									</p>
								)}

								{status === "error" && (
									<p className="text-red-600 font-medium">
										Error sending message. Try again.
									</p>
								)}
							</div>
						</form>
					</section>

					<aside className="space-y-8 pt-4">
						{/* Location Details Card */}
						<div className="bg-white p-8 rounded-xl shadow-lg border-l-4 border-gray-800">
							<h2 className="text-2xl font-americana font-bold mb-4 text-[#8a459f] ">
								Our Details
							</h2>

							<div className="space-y-5">
								{/* Address */}
								<div className="flex items-start">
									<span className="text-gray-800 mr-3 text-xl">&#9906;</span>{" "}
									{/* Pin Icon */}
									<div>
										<h3 className="text-sm uppercase font-bold text-gray-700">
											Address
										</h3>
										<p className="text-gray-600 text-base leading-relaxed">
											18136 R. J.-A.-Bombardier,
											<br /> Mirabel, QC J7J 0H5, Canada
										</p>
									</div>
								</div>

								{/* Contact */}
								<div className="flex items-start">
									<span className="text-gray-800 mr-3 text-xl">&#x2709;</span>{" "}
									{/* Mail Icon */}
									<div>
										<h3 className="text-sm uppercase font-bold text-gray-700">
											Contact
										</h3>
										<p className="text-gray-600 text-base">
											Phone:{" "}
											<span className="font-semibold text-gray-800">
												450-818-7078
											</span>
											<br />
											Email:{" "}
											<span className="font-semibold text-gray-800">
												info@AzureDress.com
											</span>
										</p>
									</div>
								</div>
							</div>
						</div>

						{/* Google Map Embed (Full width in the column) */}
						<div className="bg-white rounded-xl shadow-lg overflow-hidden border border-gray-200">
							<div className="w-full h-64 lg:h-80">
								<iframe
									title="Contact Location Map"
									className="w-full h-full border-0 grayscale hover:grayscale-0 transition-all duration-500"
									src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2793.316065600778!2d-73.92213!3d45.689148!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x4cc8d6072757f5fb%3A0x99f44a4cd27f8672!2s18136%20R.%20J.-A.-Bombardier%2C%20Mirabel%2C%20QC%20J7J%200H5%2C%20Canada!5e0!3m2!1sen!2s!4v1696942945000!5m2!1sen!2s"
									allowFullScreen=""
									loading="lazy"></iframe>
							</div>
						</div>
					</aside>
					{/* RIGHT SECTION */}
					{/* (unchanged) */}
				</div>
			</div>

			<Footer />
		</>
	);
}
